import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import { AuthProvider, useAuth } from "@/components/AuthProvider";
import { LoginForm } from "@/components/LoginForm";
import { Navigation } from "@/components/Navigation";
import ErrorBoundary from "@/components/ErrorBoundary";
import ServiceWorkerUpdate from "@/components/ServiceWorkerUpdate";
import Index from "./pages/Index";
import SendReport from "./pages/SendReport";
import SimpleIndex from "./pages/SimpleIndex";
import DebugIndex from "./pages/DebugIndex";
import Teams from "./pages/Teams";
import Users from "./pages/Users";
import ReportGroups from "./pages/ReportGroups";
import Companies from "./pages/Companies";
import ReportSummary from "./pages/ReportSummary";
import Production from "./pages/Production";
import HR from "./pages/HR";
import TeamMembers from "./pages/TeamMembers";
import EmployeeEvaluations from "./pages/EmployeeEvaluations";
import ReportUploadDemo from "./pages/ReportUploadDemo";
import ShipmentPlan from "./pages/ShipmentPlan";
import ShipmentPlanView from "./pages/ShipmentPlanView";
import NotFound from "./pages/NotFound";
import { CacheDemo } from "./components/CacheDemo";
import { OfflineBanner } from "./components/OfflineBanner";
import { Skeleton } from "@/components/ui/skeleton";
import { isAdminEmail } from "@/lib/adminConfig";

const queryClient = new QueryClient();

// Wraps routes that only admins can access — non-admins are redirected to /send-report
const AdminRoute = ({ element }: { element: React.ReactElement }) => {
  const { user } = useAuth();
  return isAdminEmail(user?.email) ? element : <Navigate to="/send-report" replace />;
};

// Wraps routes accessible by admins OR BOD
const AdminOrBODRoute = ({ element }: { element: React.ReactElement }) => {
  const { user, isBOD } = useAuth();
  return isAdminEmail(user?.email) || isBOD ? element : <Navigate to="/send-report" replace />;
};

// Shipment Plan: admin, BOD, or sales
const ShipmentRoute = ({ element }: { element: React.ReactElement }) => {
  const { user, isBOD, isSales } = useAuth();
  const ok = isAdminEmail(user?.email) || isBOD || isSales;
  return ok ? element : <Navigate to="/send-report" replace />;
};

const AppContent = () => {
  const { user, loading, isBOD } = useAuth();
  const isAdmin = isAdminEmail(user?.email);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-sm w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">Đang tải hệ thống...</h2>
          <p className="text-sm text-gray-500">Vui lòng chờ trong giây lát</p>
          
          {/* Debug info for development */}
          <div className="mt-4 text-xs text-gray-400">
            <p>Loading: {loading ? 'true' : 'false'}</p>
            <p>User: {user ? 'authenticated' : 'not authenticated'}</p>
            <p>Screen: {window.innerWidth}x{window.innerHeight}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  return (
      <div className="flex flex-col h-screen overflow-hidden">
        <Navigation />
        <OfflineBanner />
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden">
      <Routes>
        {/* Default route: admins/BOD → ReportSummary, regular users → /send-report */}
        <Route path="/" element={isAdmin || isBOD ? <ReportSummary /> : <Navigate to="/send-report" replace />} />

        {/* Public to all authenticated users */}
        <Route path="/send-report" element={<SendReport />} />

        {/* BOD đọc hết: tất cả báo cáo + summary dashboard */}
        <Route path="/reports" element={<AdminOrBODRoute element={<Index />} />} />
        {/* Admin-only routes */}
        <Route path="/production" element={<AdminRoute element={<Production />} />} />
        <Route path="/hr" element={<AdminRoute element={<HR />} />} />
        <Route path="/team-members" element={<AdminRoute element={<TeamMembers />} />} />
        <Route path="/employee-evaluations" element={<AdminRoute element={<EmployeeEvaluations />} />} />
        <Route path="/report-summary" element={<AdminOrBODRoute element={<ReportSummary />} />} />
        <Route path="/simple" element={<AdminRoute element={<SimpleIndex />} />} />
        <Route path="/debug" element={<AdminRoute element={<DebugIndex />} />} />
        <Route path="/teams" element={<AdminRoute element={<Teams />} />} />
        <Route path="/users" element={<AdminOrBODRoute element={<Users />} />} />
        <Route path="/report-groups" element={<AdminRoute element={<ReportGroups />} />} />
        <Route path="/companies" element={<AdminRoute element={<Companies />} />} />
        <Route path="/cache-demo" element={<AdminRoute element={<CacheDemo />} />} />
        <Route path="/upload-demo" element={<AdminRoute element={<ReportUploadDemo />} />} />
        <Route path="/shipment-plan" element={<ShipmentRoute element={<ShipmentPlan />} />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
        </div>
      </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/shipment-plan/view/:id"
              element={
                <AuthProvider>
                  <ShipmentPlanView />
                </AuthProvider>
              }
            />
            <Route
              path="*"
              element={
                <AuthProvider>
                  <AppContent />
                  <ServiceWorkerUpdate />
                </AuthProvider>
              }
            />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;